<template>
  <div id="wrapper">
    <ModuleSidebar @module-change="handleModuleChange" />
    <Sidebar v-if="currentModule !== 'doc' && currentModule !== 'message' && currentModule !== 'email' && currentModule !== 'app'" @navigate="handleNavigate" :current-tab="currentPage" :current-module="currentModule" />
    <DocSidebar v-else-if="currentModule === 'doc'" @navigate="handleNavigate" :current-tab="currentPage" />
    <MsgSidebar v-else-if="currentModule === 'message'" @navigate="handleNavigate" :current-tab="currentPage" />
    <MailSidebar v-else-if="currentModule === 'email'" @navigate="handleNavigate" :current-tab="currentPage" />
    <AppSidebar v-else @navigate="handleNavigate" :current-tab="currentPage" />

    <div id="content-wrapper">
      <Topbar />

      <div class="tab-bar-container">
        <div class="tab-bar" ref="tabBarRef">
          <div
            v-for="tab in visibleTabs"
            :key="tab.id"
            class="tab-item"
            :class="{ active: activeTabId === tab.id }"
            @click="switchTab(tab.id)"
          >
            <span class="tab-title">{{ tab.title }}</span>
            <button v-if="tab.id !== 'dashboard'" class="tab-close" @click.stop="closeTab(tab.id)">&times;</button>
          </div>
        </div>
        <div class="tab-actions">
          <div v-if="hiddenTabsList.length > 0" class="tab-more-wrapper" ref="tabMoreRef">
            <button class="tab-action-btn tab-more-btn" @click="toggleHiddenMenu" title="隐藏的标签页">
              <i class="fas fa-chevron-down"></i>
              <span class="tab-more-count">{{ hiddenTabsList.length }}</span>
            </button>
            <div v-if="showHiddenMenu" class="tab-hidden-menu" @click.stop>
              <div
                v-for="tab in hiddenTabsList"
                :key="tab.id"
                class="tab-hidden-item"
                :class="{ active: activeTabId === tab.id }"
                @click="selectHiddenTab(tab)"
              >
                <span class="tab-hidden-title">{{ tab.title }}</span>
                <button class="tab-hidden-close" @click.stop="closeTab(tab.id)">&times;</button>
              </div>
            </div>
          </div>
          <button class="tab-action-btn" @click="closeAllTabs" title="关闭所有标签页">
            <i class="fas fa-times-circle"></i>
          </button>
        </div>
      </div>

      <div class="main-content">
        <component :is="currentComponent" :key="componentKey" :tab-payload="currentTabPayload" @navigate="handleNavigate" />
      </div>


    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick, provide } from 'vue'
import { useRouter } from 'vue-router'
import { initSession, destroySession } from '../utils/sessionManager'
import Dashboard from '../views/Dashboard.vue'
import Users from '../views/Users.vue'
import Roles from '../views/Roles.vue'
import Organizations from '../views/Organizations.vue'
import Permissions from '../views/Permissions.vue'
import Logs from '../views/Logs.vue'
import Announcements from '../views/Announcements.vue'
import DatabaseManager from '../views/DatabaseManager.vue'
import SqlExecution from '../views/SqlExecution.vue'
import LogQuery from '../views/LogQuery.vue'
import ProcessManagement from '../views/ProcessManagement.vue'
import JobScheduled from '../views/JobScheduled.vue'
import PersonalInfo from '../views/PersonalInfo.vue'
import PasswordChange from '../views/PasswordChange.vue'
import ProxySettings from '../views/ProxySettings.vue'
import QuickLinkManager from '../views/PersonalCenter/QuickLinkManager.vue'
import PendingTasks from '../views/PendingTasks.vue'
import TaskView from '../views/TaskView.vue'
import LaunchableProcesses from '../views/LaunchableProcesses.vue'
import Drafts from '../views/Drafts.vue'
import CompletedTasks from '../views/CompletedTasks.vue'
import SubmittedTasks from '../views/SubmittedTasks.vue'
import ViewableTasks from '../views/ViewableTasks.vue'
import CcTasks from '../views/CcTasks.vue'
import TaskManagement from '../views/TaskManagement.vue'
import ModuleManagement from '../views/ModuleManagement.vue'
import SystemManagement from '../views/SystemManagement.vue'
import CompanyManagement from '../views/CompanyManagement.vue'
import TenantManagement from '../views/TenantManagement.vue'
import SystemReports from '../views/SystemReports.vue'
import CustomReports from '../views/CustomReports.vue'
import ReportManagement from '../views/ReportManagement.vue'
import AaPermissions from '../views/AaPermissions.vue'
import BaPermissions from '../views/BaPermissions.vue'
import LaPermissions from '../views/LaPermissions.vue'
import DaPermissions from '../views/DaPermissions.vue'
import DocOverview from '../views/DocOverview.vue'
import DocLibrary from '../views/DocLibrary.vue'
import DocAudit from '../views/DocAudit.vue'
import DocPermission from '../views/DocPermission.vue'
import MsgOverview from '../views/MsgOverview.vue'
import MsgChat from '../views/MsgChat.vue'
import MsgContacts from '../views/MsgContacts.vue'
import MailOverview from '../views/MailOverview.vue'
import MailInternal from '../views/MailInternal.vue'
import MailCompose from '../views/MailCompose.vue'
import MailExternal from '../views/MailExternal.vue'
import MailExtCompose from '../views/MailExtCompose.vue'
import MailSettings from '../views/MailSettings.vue'
import AppOverview from '../views/AppOverview.vue'
import AppUsers from '../views/AppUsers.vue'
import AppDevices from '../views/AppDevices.vue'
import AppLogs from '../views/AppLogs.vue'
import FormDesigner from '../views/develop/FormDesigner.vue'
import OldFormDesigner from '../views/develop/OldFormDesigner.vue'
import ProcessDesigner from '../views/develop/ProcessDesigner.vue'
import { recordPageView } from '../api/audit-log'
import Sidebar from './Sidebar.vue'
import DocSidebar from './DocSidebar.vue'
import MsgSidebar from './MsgSidebar.vue'
import MailSidebar from './MailSidebar.vue'
import AppSidebar from './AppSidebar.vue'
import Topbar from './Topbar.vue'
import ModuleSidebar from './ModuleSidebar.vue'

let resizeObserver = null

onMounted(() => {
  initSession()
  nextTick(() => {
    calcOverflow()
    if (tabBarRef.value) {
      resizeObserver = new ResizeObserver(() => nextTick(calcOverflow))
      resizeObserver.observe(tabBarRef.value)
    }
    document.addEventListener('click', handleClickOutside)
  })
})

onUnmounted(() => {
  destroySession()
  if (resizeObserver) { resizeObserver.disconnect(); resizeObserver = null }
  document.removeEventListener('click', handleClickOutside)
})

const currentPage = ref('dashboard')
const currentModule = ref('bpm')

const componentMap = {
  'dashboard': Dashboard,
  'users': Users,
  'roles': Roles,
  'organizations': Organizations,
  'permissions': Permissions,
  'logs': Logs,
  'announcements': Announcements,
  'databaseManager': DatabaseManager,
  'sqlExecution': SqlExecution,
  'logQuery': LogQuery,
  'processManagement': ProcessManagement,
  'jobManagement': JobScheduled,
  'personalInfo': PersonalInfo,
  'passwordChange': PasswordChange,
  'proxySettings': ProxySettings,
  'quickLinkManager': QuickLinkManager,
  'pendingTasks': PendingTasks,
  'taskView': TaskView,
  'launchableProcesses': LaunchableProcesses,
  'drafts': Drafts,
  'completedTasks': CompletedTasks,
  'submittedTasks': SubmittedTasks,
  'viewableTasks': ViewableTasks,
  'ccTasks': CcTasks,
  'taskManagement': TaskManagement,
  'moduleManagement': ModuleManagement,
  'systemManagement': SystemManagement,
  'companyManagement': CompanyManagement,
  'tenantManagement': TenantManagement,
  'systemReports': SystemReports,
  'customReports': CustomReports,
  'reportManagement': ReportManagement,
  'aaPermissions': AaPermissions,
  'baPermissions': BaPermissions,
  'laPermissions': LaPermissions,
  'daPermissions': DaPermissions,
  'docOverview': DocOverview,
  'docLibrary': DocLibrary,
  'docAudit': DocAudit,
  'docPermission': DocPermission,
  'msgOverview': MsgOverview,
  'msgChat': MsgChat,
  'msgContacts': MsgContacts,
  'mailOverview': MailOverview,
  'mailInternal': MailInternal,
  'mailCompose': MailCompose,
  'mailExternal': MailExternal,
  'mailExtCompose': MailExtCompose,
  'mailSettings': MailSettings,
  'appOverview': AppOverview,
  'appUsers': AppUsers,
  'appDevices': AppDevices,
  'appLogs': AppLogs,
  'formDesigner': FormDesigner,
  'oldFormDesigner': OldFormDesigner,
  'processDesigner': ProcessDesigner,
  'taskView': TaskView
}

const titleMap = {
  'dashboard': '首页',
  'users': '用户管理',
  'roles': '角色管理',
  'organizations': '组织管理',
  'permissions': '权限管理',
  'logs': '操作日志',
  'announcements': '公告管理',
  'databaseManager': '数据库管理',
  'sqlExecution': '语句执行',
  'logQuery': '日志查询',
  'processManagement': '流程管理',
  'jobManagement': '定时作业管理',
  'personalInfo': '个人信息',
  'passwordChange': '密码修改',
  'proxySettings': '代理设置',
  'quickLinkManager': '快捷入口',
  'pendingTasks': '待处理',
  'launchableProcesses': '可发起',
  'drafts': '草稿箱',
  'completedTasks': '已处理任务',
  'submittedTasks': '已提交任务',
  'viewableTasks': '可查看任务',
  'ccTasks': '抄送任务',
  'taskManagement': '任务管理',
  'moduleManagement': '模块管理',
  'systemManagement': '系统管理',
  'companyManagement': '公司管理',
  'tenantManagement': '租户管理',
  'systemReports': '系统报表',
  'customReports': '自定义报表',
  'reportManagement': '报表管理',
  'aaPermissions': '公告权限',
  'baPermissions': '模块权限',
  'laPermissions': '流程权限',
  'daPermissions': '报表权限',
  'docOverview': '文档概览',
  'docLibrary': '文档库',
  'docAudit': '文档审批',
  'docPermission': '文档权限',
  'msgOverview': '消息总览',
  'msgChat': '消息聊天',
  'msgContacts': '通讯录',
  'mailOverview': '邮件概览',
  'mailInternal': '内邮收件箱',
  'mailCompose': '写邮件(内邮)',
  'mailExternal': '外邮收件箱',
  'mailExtCompose': '写邮件(外邮)',
  'mailSettings': '邮箱配置',
  'appOverview': 'APP概览',
  'appUsers': '用户授权',
  'appDevices': '设备管理',
  'appLogs': '操作日志',
  'formDesigner': '表单设计',
  'oldFormDesigner': '经典表单设计',
  'processDesigner': '流程设计',
  'taskView': '任务预览',
  'production/workorder': '工单排产',
  'production/outsource': '委外工单排产',
  'production/summary': '工单排产汇总表',
  'purchase/request': '添加采购需求',
  'purchase/shortage': '材料欠料表',
  'purchase/schedule': '采购进货排程',
  'purchase/delivery': '厂商回复交期'
}

const tabs = ref([
  { id: 'dashboard', title: '首页', componentName: 'dashboard' }
])
const activeTabId = ref('dashboard')
const tabBarRef = ref(null)
const tabMoreRef = ref(null)
const showHiddenMenu = ref(false)
const tabMeasuredWidths = ref(0)

const TAB_MIN_WIDTH = 80
const TAB_MAX_WIDTH = 160
const TAB_GAP = 3
const ACTIONS_RESERVED = 90

// 根据可用宽度计算可见标签和隐藏标签
const visibleTabs = computed(() => {
   const w = tabMeasuredWidths.value
   if (!w) return tabs.value.slice(0, 1)
  const available = w - ACTIONS_RESERVED
  if (available <= 0) return [tabs.value[0]]
  // active tab must be visible
  let visible = [tabs.value[0]]
  let used = TAB_MAX_WIDTH + TAB_GAP
  for (let i = 1; i < tabs.value.length; i++) {
    const t = tabs.value[i]
    const est = Math.min(TAB_MAX_WIDTH, Math.max(TAB_MIN_WIDTH, (t.title || '').length * 14 + 50))
    if (used + est <= available || t.id === activeTabId.value) {
      visible.push(t)
      if (t.id === activeTabId.value) {
        // ensure active tab fits; if not, remove others from end
        let extra = used + est - available
        while (extra > 0 && visible.length > 1) {
          const last = visible[visible.length - 2]
          if (last.id === 'dashboard') break
          const lastEst = Math.min(TAB_MAX_WIDTH, Math.max(TAB_MIN_WIDTH, (last.title || '').length * 14 + 50))
          extra -= lastEst + TAB_GAP
          visible.splice(visible.length - 2, 1)
        }
      }
      used += est + TAB_GAP
    } else {
      break
    }
  }
  return visible
})

const hiddenTabsList = computed(() => {
  const vIds = new Set(visibleTabs.value.map(t => t.id))
  return tabs.value.filter(t => !vIds.has(t.id))
})

const calcOverflow = () => {
  const bar = tabBarRef.value
  if (bar) tabMeasuredWidths.value = bar.clientWidth
}

const toggleHiddenMenu = () => {
  showHiddenMenu.value = !showHiddenMenu.value
}

const selectHiddenTab = (tab) => {
  showHiddenMenu.value = false
  switchTab(tab.id)
}

// 点击外部关闭下拉
const handleClickOutside = (e) => {
  if (showHiddenMenu.value && tabMoreRef.value && !tabMoreRef.value.contains(e.target)) {
    showHiddenMenu.value = false
  }
}

const closeAllTabs = () => {
  tabs.value = [{ id: 'dashboard', title: '首页', componentName: 'dashboard' }]
  activeTabId.value = 'dashboard'
  currentPage.value = 'dashboard'
}

const currentComponent = computed(() => {
  const tab = tabs.value.find(t => t.id === activeTabId.value)
  const componentName = tab ? tab.componentName : 'dashboard'
  return componentMap[componentName] || Dashboard
})

// 强制组件重挂载的 key：每次打开同一类型 tab 时使用不同的 _ts 值
const componentKey = computed(() => {
  const tab = tabs.value.find(t => t.id === activeTabId.value)
  return (tab?._ts) ? `${tab.id}_${tab._ts}` : tab?.id || 'dashboard'
})

// 当前 tab 的 payload（用于传递给 TaskView 等组件）
const currentTabPayload = computed(() => {
  const tab = tabs.value.find(t => t.id === activeTabId.value)
  return tab?.payload || {}
})

// 通过 provide 传递当前 tab 的 payload 给子组件
provide('tabPayload', currentTabPayload)

const handleNavigate = (page) => {
  currentPage.value = page

  // 记录页面访问日志（不阻塞导航）
  const pageTitle = titleMap[page] || page
  recordPageView({
    Module: pageTitle,
    Action: `进入页面: ${pageTitle}`,
    RequestUrl: `/page/${page}`,
    RequestMethod: 'PAGE_VIEW'
  }).catch(() => {})

  const existingTab = tabs.value.find(t => t.id === page)
  if (existingTab) {
    activeTabId.value = page
  } else {
    tabs.value.push({
      id: page,
      title: titleMap[page] || page,
      componentName: page
    })
    activeTabId.value = page
  }
}

const switchTab = (tabId) => {
  // 切换 tab 时，如果是任务视图，更新时间戳强制组件重新挂载
  const tab = tabs.value.find(t => t.id === tabId)
  if (tab && tab.componentName === 'taskView') {
    tab._ts = Date.now()
  }
  activeTabId.value = tabId
  currentPage.value = tabId
}

const closeTab = (tabId) => {
  if (tabId === 'dashboard' || tabs.value.length <= 1) return
  showHiddenMenu.value = false
  const index = tabs.value.findIndex(t => t.id === tabId)
  tabs.value.splice(index, 1)
  if (activeTabId.value === tabId) {
    activeTabId.value = tabs.value[Math.min(index, tabs.value.length - 1)].id
    currentPage.value = activeTabId.value
  }
}

const router = useRouter()

const handleModuleChange = (moduleId) => {
  currentModule.value = moduleId
  
  if (moduleId === 'ai') {
    router.push('/ai-chat')
    return
  }

  if (moduleId === 'doc') {
    tabs.value = [{ id: 'docOverview', title: '文档概览', componentName: 'docOverview' }]
    activeTabId.value = 'docOverview'
    currentPage.value = 'docOverview'
    return
  }

  if (moduleId === 'message') {
    tabs.value = [{ id: 'msgOverview', title: '消息总览', componentName: 'msgOverview' }]
    activeTabId.value = 'msgOverview'
    currentPage.value = 'msgOverview'
    return
  }

  if (moduleId === 'email') {
    tabs.value = [{ id: 'mailOverview', title: '邮件概览', componentName: 'mailOverview' }]
    activeTabId.value = 'mailOverview'
    currentPage.value = 'mailOverview'
    return
  }

  if (moduleId === 'app') {
    tabs.value = [{ id: 'appOverview', title: 'APP概览', componentName: 'appOverview' }]
    activeTabId.value = 'appOverview'
    currentPage.value = 'appOverview'
    return
  }

  tabs.value = [{ id: 'dashboard', title: '首页', componentName: 'dashboard' }]
  activeTabId.value = 'dashboard'
  currentPage.value = 'dashboard'
}

function navigateToProcessDesigner() {
  const p = window.currentEditingProcess
  const tabId = 'processDesigner'
  const tabTitle = p ? '流程设计-' + p.name : '流程设计'
  const ts = Date.now()
  // Always close old tab and create new one to force component remount
  tabs.value = tabs.value.filter(t => t.id !== tabId)
  tabs.value.push({ id: tabId, title: tabTitle, componentName: 'processDesigner', _ts: ts })
  activeTabId.value = tabId
  currentPage.value = 'processDesigner'
}

function navigateToFormDesigner() {
  const p = window.currentEditingProcess
  const tabId = 'formDesigner'
  const tabTitle = p ? '表单设计-' + p.name : '表单设计'
  const ts = Date.now()
  // Always close old tab and create new one to force component remount
  tabs.value = tabs.value.filter(t => t.id !== tabId)
  tabs.value.push({ id: tabId, title: tabTitle, componentName: 'formDesigner', _ts: ts })
  activeTabId.value = tabId
  currentPage.value = 'formDesigner'
}

function navigateToOldFormDesigner() {
  const p = window.currentEditingProcess
  const tabId = 'oldFormDesigner'
  const tabTitle = p ? '经典表单-' + p.name : '经典表单设计'
  const ts = Date.now()
  // Always close old tab and create new one to force component remount
  tabs.value = tabs.value.filter(t => t.id !== tabId)
  tabs.value.push({ id: tabId, title: tabTitle, componentName: 'oldFormDesigner', _ts: ts })
  activeTabId.value = tabId
  currentPage.value = 'oldFormDesigner'
}

function navigateToProcessManagement() {
  currentPage.value = 'processManagement'
  const existingTab = tabs.value.find(t => t.id === 'processManagement')
  if (existingTab) {
    activeTabId.value = 'processManagement'
  } else {
    tabs.value.push({ id: 'processManagement', title: '流程管理', componentName: 'processManagement' })
    activeTabId.value = 'processManagement'
  }
}

function navigateToTaskView(payload) {
  const taskId = payload.taskId || payload.taskData?.taskId || ''
  const tabId = `taskView_${taskId}` // 每个任务使用独立的 tab ID
  const tabTitle = payload.title || '任务预览'
  
  const existingTab = tabs.value.find(t => t.id === tabId)
  if (existingTab) {
    // 已存在该任务的 tab，更新时间戳强制组件重新挂载
    existingTab._ts = Date.now()
    activeTabId.value = tabId
  } else {
    // 创建新 tab，存储 payload 到 tab 数据中
    tabs.value.push({ 
      id: tabId, 
      title: tabTitle, 
      componentName: 'taskView', 
      _ts: Date.now(),
      payload: payload // 存储任务数据到 tab 中
    })
    activeTabId.value = tabId
  }
  currentPage.value = 'taskView'
}

window.addEventListener('navigateToFormDesigner', navigateToFormDesigner)
window.addEventListener('navigateToOldFormDesigner', navigateToOldFormDesigner)
window.addEventListener('navigateToProcessDesigner', navigateToProcessDesigner)
window.addEventListener('navigateToProcessManagement', navigateToProcessManagement)
window.addEventListener('navigateToTaskView', (e) => navigateToTaskView(e.detail))
window.addEventListener('navigateTo', (e) => handleNavigate(e.detail))
</script>
